import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@kizen/kds/Button';
import { ControlWrapper } from './styles';
import { deferExecution } from 'utility/defer';
import { KZN_FRAME_EVENTS } from '__pages/BusinessBuilderPublic/events';

type ControlsProps = {
  isMobile: boolean;
  chatPadding: string;
  setNewMessage: (message: string) => void;
  onSendMessage: (message: string, isCustomMessage?: boolean) => void;
  onCreateNewChat: () => void;
  setDisplayAlertToast: () => void;
  newMessage: string;
  onCompleteSession: () => void;
  showBuildApplicationButton: boolean;
  showSkipQuestionsButton: boolean;
  showAskMoreQuestionsButton: boolean;
  showCreateNewChatButton: boolean;
  disableBuildApplicationButton: boolean;
  disableSkipQuestionsButton: boolean;
  disableCreateNewChatButton: boolean;
  disableAskMoreQuestionsButton: boolean;
  disableSendMessageButton: boolean;
  scrollToBottom: () => void;
  entityName?: string;
  trackEvent?: (event: string, payload?: any) => void;
};
const BUSINESS_BUILDER_BUTTONS = {
  CREATE_BUSINESS_PLAN: {
    buttonText: (t: any) => t('Generate my requirements'),
    message:
      'With the information already provided, generate my application requirements',
  },
  START_OVER: {
    buttonText: (t: any) => t('Start Over'),
    message: 'Start Over',
  },
  ASK_MORE_QUESTIONS: {
    buttonText: (t: any) => t('Ask more questions'),
    message: 'Ask more questions to refine my business generation plan',
  },
};

export const Controls: FC<ControlsProps> = ({
  isMobile,
  chatPadding,
  setNewMessage,
  onSendMessage,
  onCreateNewChat,
  setDisplayAlertToast,
  newMessage,
  onCompleteSession,
  showBuildApplicationButton,
  showSkipQuestionsButton,
  showAskMoreQuestionsButton,
  showCreateNewChatButton,
  disableBuildApplicationButton,
  disableSkipQuestionsButton,
  disableCreateNewChatButton,
  disableAskMoreQuestionsButton,
  disableSendMessageButton,
  scrollToBottom,
  entityName,
  trackEvent,
}) => {
  const { t } = useTranslation();

  const handleSendDefinedMessage = useCallback(
    (message: string, isCustomMessage?: boolean) => {
      onSendMessage(message, isCustomMessage);
      scrollToBottom();
      setNewMessage('');
    },
    [onSendMessage, setNewMessage, scrollToBottom]
  );

  const handleCreateNewChat = () => {
    trackEvent?.(KZN_FRAME_EVENTS.START_OVER);
    onCreateNewChat();
    setNewMessage('');
    deferExecution(() => setDisplayAlertToast());
  };

  const handleSendMessage = () => {
    trackEvent?.(KZN_FRAME_EVENTS.CHAT_USER);
    return handleSendDefinedMessage(newMessage, newMessage !== '');
  };

  const handleCompleteSession = () => {
    trackEvent?.(KZN_FRAME_EVENTS.BUILD);
    return onCompleteSession();
  };

  const handleCreatePlan = () => {
    trackEvent?.(KZN_FRAME_EVENTS.GENERATE_REQUIREMENTS);
    return handleSendDefinedMessage(
      BUSINESS_BUILDER_BUTTONS.CREATE_BUSINESS_PLAN.message
    );
  };

  const handleAskMoreQuestions = () => {
    trackEvent?.(KZN_FRAME_EVENTS.ASK_MORE_QUESTIONS);
    return handleSendDefinedMessage(
      BUSINESS_BUILDER_BUTTONS.ASK_MORE_QUESTIONS.message
    );
  };

  return (
    <ControlWrapper chatPadding={chatPadding} isMobile={isMobile}>
      {showCreateNewChatButton ? (
        <Button
          type="button"
          variant="text"
          color="warning"
          onClick={handleCreateNewChat}
          disabled={disableCreateNewChatButton}
        >
          {BUSINESS_BUILDER_BUTTONS.START_OVER.buttonText(t)}
        </Button>
      ) : null}
      {showAskMoreQuestionsButton ? (
        <Button
          type="button"
          variant="text"
          color="secondary"
          onClick={handleAskMoreQuestions}
          disabled={disableAskMoreQuestionsButton}
        >
          {BUSINESS_BUILDER_BUTTONS.ASK_MORE_QUESTIONS.buttonText(t)}
        </Button>
      ) : null}
      {showSkipQuestionsButton ? (
        <Button
          variant="text"
          color="primary"
          type="button"
          maxWidth={false}
          onClick={handleCreatePlan}
          disabled={disableSkipQuestionsButton}
        >
          {BUSINESS_BUILDER_BUTTONS.CREATE_BUSINESS_PLAN.buttonText(t)}
        </Button>
      ) : null}
      <Button
        color="secondary"
        onClick={handleSendMessage}
        disabled={disableSendMessageButton}
        type="submit"
      >
        {t('Send Message')}
      </Button>
      {showBuildApplicationButton ? (
        <Button
          type="button"
          onClick={handleCompleteSession}
          disabled={disableBuildApplicationButton}
        >
          {entityName
            ? t('Build My {{entityName}}', { entityName })
            : t('Build')}
        </Button>
      ) : null}
    </ControlWrapper>
  );
};
